import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Home,
  Users,
  FileText,
  Calendar,
  CalendarDays,
  DollarSign,
  CheckSquare,
  Clock,
  Settings,
  CreditCard,
  HelpCircle,
  BookOpen,
  LogOut,
  ChevronRight,
  Crown,
  ArrowRight,
} from 'lucide-react';
import { APP_LOGO, APP_NAME } from './appBrand';
import Wordmark from '../Wordmark';
import { isWix } from '../../utils/platform';
import { DOCS_URL } from '../../support';

/**
 * The sidebar.
 *
 * Grouped rather than listed. Twelve rows in one run read as a menu to search;
 * the same twelve under MAIN / SUPPORT / ACCOUNT / RESOURCES read as four short
 * lists, and only one of them is what a merchant came for. The headings also
 * give the two things that are not navigation - the help card and the seat card
 * - somewhere to sit that is not "another row in the menu".
 *
 * Every label goes through t(). They were hardcoded English before, in an app
 * that ships nine languages: a German merchant read a German page with an
 * English rail down the side of it.
 */
const Navigation = ({ onLogout, stats }) => {
  const location = useLocation();
  const { t } = useTranslation();

  // staff_limit is a number or the string "Unlimited"; both have to render.
  const seats = (() => {
    if (!stats) return null;
    const used = Number(stats.total_staff) || 0;
    const raw = stats.staff_limit;

    if (typeof raw === 'string' && raw.trim().toLowerCase() === 'unlimited') {
      return { label: String(used), percent: null, full: false, left: null };
    }

    const limit = Number(raw);
    if (!Number.isFinite(limit) || limit <= 0) return null;

    return {
      label: `${used} / ${limit}`,
      percent: Math.min(100, Math.round((used / limit) * 100)),
      full: used >= limit,
      left: Math.max(0, limit - used),
    };
  })();

  const navigationItems = [
    { href: '/', label: t('home'), icon: Home },
    { href: '/StaffManagement', label: t('staff'), icon: Users },
    { href: '/Reports', label: t('reports'), icon: FileText },
    { href: '/Schedule', label: t('schedule'), icon: Calendar },
    { href: '/Payroll', label: t('payroll'), icon: DollarSign },
    { href: '/Tasklist', label: t('tasklist'), icon: CheckSquare },
    { href: '/TimeOff', label: t('time_off'), icon: Clock },
    // Plans is injected below - hidden on Wix (see platform.js).
    { href: '/Settings', label: t('settings'), icon: Settings },
  ];

  // Wix App Market apps must bill through Wix, not our Stripe checkout, so the
  // Plans page is removed entirely from the Wix build of the dashboard.
  if (!isWix()) {
    navigationItems.splice(7, 0, { href: '/Pricing', label: t('plans'), icon: CreditCard });
  }

  const faqActive = location.pathname.startsWith('/Faq');

  return (
    <nav className="sidebar-nav">
      <div className="nav-header">
        <div className="nav-brand">
          <img src={APP_LOGO} alt={APP_NAME} className="nav-brand-logo" />
          {/* The same component the welcome screen, the setup guide's footer
              and the page footer all draw, rather than a second spelling of the
              name in markup of its own. */}
          <Wordmark className="nav-brand-mark" />
        </div>
      </div>

      <p className="nav-section">{t('nav_main')}</p>

      <div className="nav-menu">
        {navigationItems.map((item) => {
          const isActive =
            location.pathname === item.href ||
            (item.href !== '/' && location.pathname.startsWith(item.href));
          const IconComponent = item.icon;

          return (
            <div key={item.href} className="nav-item">
              <Link
                to={item.href}
                className={`nav-link ${isActive ? 'active' : ''}`}
                aria-current={isActive ? 'page' : undefined}
              >
                <IconComponent size={18} strokeWidth={isActive ? 2.25 : 2} />
                <span className="nav-text">{item.label}</span>
                {/* Only on the current row, and hidden from screen readers:
                    aria-current already says which page this is, and a chevron
                    announced as "chevron right" says nothing. */}
                {isActive && (
                  <ChevronRight size={15} strokeWidth={2.25} aria-hidden="true" className="nav-chev" />
                )}
              </Link>
            </div>
          );
        })}
      </div>

      <p className="nav-section">{t('nav_support')}</p>

      {/* Help is a card, not a thirteenth row. It is the one thing in this
          column a stuck merchant is looking for, and a row in a list of twelve
          is the easiest place on the screen to miss. */}
      <Link to="/Faq" className={`nav-card${faqActive ? ' is-active' : ''}`}>
        <span className="nav-card__disc" aria-hidden="true">
          <HelpCircle size={17} strokeWidth={2} />
        </span>
        <span className="nav-card__text">
          <span className="nav-card__title">{t('help_faq')}</span>
          <span className="nav-card__sub">{t('get_answers_and_support')}</span>
        </span>
        <ChevronRight size={15} strokeWidth={2.25} aria-hidden="true" className="nav-card__chev" />
      </Link>

      {/* Seat usage. The one number in this column the owner can act on, with
          the action under it. staff_limit comes back as a number OR the string
          "Unlimited", and an unlimited plan shows the headcount with no bar and
          no upsell - there is no ratio to draw and nothing to sell. */}
      {seats && (
        <>
          <p className="nav-section">{t('nav_account')}</p>

          <div className="nav-seats">
            <div className="nav-seats-row">
              <span className="nav-seats-label">
                <span className="nav-seats-crown" aria-hidden="true">
                  <Crown size={14} strokeWidth={2} />
                </span>
                {t('seats_used')}
              </span>
              <span className="nav-seats-value">{seats.label}</span>
            </div>

            {seats.percent !== null && (
              <>
                <div className="nav-seats-track">
                  <span
                    className={`nav-seats-fill${seats.full ? ' is-full' : ''}`}
                    style={{ width: `${seats.percent}%` }}
                  />
                </div>

                {/* The bar in words, so the state survives greyscale and colour
                    blindness rather than living only in a red fill. */}
                <p className="nav-seats-note">
                  {seats.full
                    ? t('seats_all_used')
                    : t('seats_available_count', { remaining: seats.left })}
                </p>
              </>
            )}

            {seats.percent !== null && !isWix() && (
              <Link className="nav-seats-cta" to="/Pricing">
                {t('upgrade_plan')}
                <ArrowRight size={15} strokeWidth={2.25} aria-hidden="true" />
              </Link>
            )}
          </div>
        </>
      )}

      <p className="nav-section">{t('nav_resources')}</p>

      {/* These three used to be a 215px band of cards across the Home page,
          below the fold, on one route only - while this column sat empty on
          every route. Here they cost no page height and are reachable from
          anywhere. */}
      <div className="nav-rail">
        <a className="nav-rail-link" href={DOCS_URL} target="_blank" rel="noopener noreferrer">
          <BookOpen size={17} strokeWidth={2} />
          <span>{t('documentation')}</span>
        </a>

        <a
          className="nav-rail-link"
          href="https://calendly.com/talk-to-specialist/30min"
          target="_blank"
          rel="noopener noreferrer"
        >
          <CalendarDays size={17} strokeWidth={2} />
          <span>{t('book_consultation')}</span>
        </a>

        <button type="button" className="nav-rail-link nav-rail-link--logout" onClick={onLogout}>
          <LogOut size={17} strokeWidth={2} />
          <span>{t('logout')}</span>
        </button>
      </div>
    </nav>
  );
};

export default Navigation;
